import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Button, LinearProgress, Menu, MenuItem, TextField } from '@mui/material';
import {
	DataGrid,
	GridCsvExportMenuItem,
	GridExportMenuItemProps,
	GridPrintExportMenuItem,
	GridToolbarExportContainer,
} from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { API } from '~/api';
import { AppRoutes } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { FragranceService } from '~/service/service.fragrance';
import { UIService } from '~/service/service.ui';
import { FTSStrategy } from '~/shared/dto/paging.dto';
import { Enum } from '~/shared/fields';
import { useService } from '~/shared/service/service.base';
import { FragrancesImpressionsDialog } from '~/view/dialog/dialog.fragrances.impressions';
import { ImportFragranceDialog, ImportFragrancePhotosDialog } from '~/view/dialog/dialog.import';
import {
	ActionsColumn,
	ArrayColumn,
	AvatarColumn,
	CheckedColumn,
	DateTimeColumn,
	EnumColumn,
	LinkColumn,
	useGridColumns,
} from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { GridToolbarView } from '~/view/grid/grid.toolbar';
import { Row } from '~/view/view.box';
import { PerformTimeView } from '~/view/view.perform.time';

export const FragranceListPage = observer(() => {
	const [fragrances, ui, auth] = useService(FragranceService, UIService, AuthService);
	const columns = useGridColumns<API.FragranceItem>(() => [
		AvatarColumn({
			field: 'photo',
			headerName: 'Photo',
			filterable: false,
			tooltip: row => row.relevance,
		}),
		CheckedColumn({ field: 'active', headerName: 'Active', filterable: false }),
		CheckedColumn({ field: 'discontinued', headerName: 'Discontinued', filterable: false }),
		{ field: 'id', headerName: 'ID', filterable: false },
		{ field: 'name', headerName: 'Name', flex: 1, minWidth: 200, filterable: false },
		{ field: 'brandName', headerName: 'Brand', minWidth: 150, filterable: true },
		DateTimeColumn({ field: 'photoUpdateAt', headerName: 'Photo updated at' }),
		{ field: 'asinActiveCount', headerName: 'ASIN' },
		EnumColumn('Longevity', { field: 'longevity', headerName: 'Longevity', width: 100, filterable: false }),
		ArrayColumn({ field: 'olfactiveNames', headerName: 'Olfactive', width: 150 }),
		EnumColumn('Sillage', { field: 'sillage', headerName: 'Sillage', minWidth: 100, filterable: false }),
		ArrayColumn({ field: 'years', headerName: 'Years' }),
		ArrayColumn({ field: 'perfumerNames', headerName: 'Perfumers', minWidth: 100 }),
		{ field: 'description', headerName: 'Description', flex: 1, minWidth: 200, filterable: false },
		LinkColumn({
			field: 'superId',
			headerName: 'Super',
			getLink: row => AppRoutes.FragranceEdit.buildPath({ id: row.superId ?? 0 }),
		}),
		ActionsColumn({
			editRoute: AppRoutes.FragranceEdit.buildPath,
			onDelete: fragrances.delete,
			permission: 'fragrance',
		}),
	]);

	ui.usePageTitle('Fragrances');
	useEffect(() => fragrances.loadAndSubscribe(), [fragrances]);

	return (
		<GridContainer async={fragrances.async}>
			<DataGrid
				initialState={fragrances.gridState.initial()}
				loading={fragrances.async.loading}
				rowCount={fragrances.totalCount}
				rows={fragrances.items.slice()}
				columns={columns}
				density={fragrances.gridState.density}
				paginationMode="server"
				filterMode="server"
				sortingMode="server"
				onStateChange={fragrances.gridState.onStateChange}
				onFilterModelChange={fragrances.changeFilterModel}
				onPaginationModelChange={fragrances.gridState.onPaginationModelChange}
				paginationModel={fragrances.gridState.paginationModel}
				slots={{
					loadingOverlay: LinearProgress,
					noRowsOverlay: () => <GridNoRows>No fragrances found</GridNoRows>,
					pagination: MuiGridPagination,
					toolbar: () => (
						<GridToolbarView
							onReload={fragrances.reload}
							gridState={fragrances.gridState}
							onCreateUrl={
								auth.can('fragrance', 'CREATE') ? AppRoutes.FragranceNew.buildPath({}) : undefined
							}
							exportEnabled={false}
							rightMenu={<RightMenu />}
						>
							<Row ml={4} minWidth={300} gap={2}>
								<TextField
									select
									fullWidth
									label="Search strategy"
									value={fragrances.searchStrategy}
									onChange={fragrances.changeSearchStrategy}
								>
									{Enum.keys(FTSStrategy).map(s => (
										<MenuItem key={s} value={s}>
											{s}
										</MenuItem>
									))}
								</TextField>
							</Row>
						</GridToolbarView>
					),
				}}
			/>
			<PerformTimeView time={fragrances.searchTime} />
			<ImportFragranceDialog />
			<ImportFragrancePhotosDialog />
		</GridContainer>
	);
});

const RightMenu = observer(() => {
	const [ui, auth] = useService(UIService, AuthService);
	const btnRecalcRef = useRef<HTMLButtonElement>(null);
	const [menuRecalcOpen, menuRecalcToggle] = useReducer(v => !v, false);

	const handleClickImpressions = useCallback(() => {
		menuRecalcToggle();
		ui.showDialog(FragrancesImpressionsDialog);
	}, [ui]);

	return (
		<Row>
			{auth.can('fragrance', 'UPDATE') ? (
				<>
					<Button ref={btnRecalcRef} size="small" startIcon={<RestartAltIcon />} onClick={menuRecalcToggle}>
						Recalculate
					</Button>
					<Menu open={menuRecalcOpen} anchorEl={btnRecalcRef.current} onClose={menuRecalcToggle}>
						<MenuItem onClick={handleClickImpressions}>Impressions</MenuItem>
					</Menu>
					<FragrancesImpressionsDialog />
				</>
			) : null}
			{auth.can('fragrance', 'EXPORT') ? <GridToolbarExport /> : null}
		</Row>
	);
});

function ImagesExportMenuItem(props: GridExportMenuItemProps<{}>) {
	const [fragrances] = useService(FragranceService);
	const { hideMenu } = props;

	return (
		<MenuItem
			onClick={() => {
				fragrances.exportImages();
				hideMenu?.();
			}}
		>
			Export Images
		</MenuItem>
	);
}

function ImportCSVMenuItem(props: GridExportMenuItemProps<{}>) {
	const [ui] = useService(UIService);
	const { hideMenu } = props;

	return (
		<MenuItem
			onClick={() => {
				hideMenu?.();
				ui.showDialog(ImportFragranceDialog);
			}}
		>
			Import from CSV
		</MenuItem>
	);
}

function ImportPhotosCSVMenuItem(props: GridExportMenuItemProps<{}>) {
	const [ui] = useService(UIService);
	const { hideMenu } = props;

	return (
		<MenuItem
			onClick={() => {
				hideMenu?.();
				ui.showDialog(ImportFragrancePhotosDialog);
			}}
		>
			Import photos from CSV
		</MenuItem>
	);
}

function ExportCSVMenuItem(props: GridExportMenuItemProps<{}>) {
	const [fragSvc, authSvc] = useService(FragranceService, AuthService);
	const { hideMenu } = props;

	return authSvc.can('fragrance', 'EXPORT') ? (
		<MenuItem
			onClick={() => {
				hideMenu?.();
				fragSvc.exportCSV();
			}}
		>
			Export to CSV
		</MenuItem>
	) : null;
}

function GridToolbarExport({ csvOptions, printOptions, ...other }: any) {
	return (
		<GridToolbarExportContainer {...other}>
			<GridCsvExportMenuItem options={csvOptions} />
			<GridPrintExportMenuItem options={printOptions} />
			<ImagesExportMenuItem />
			<ExportCSVMenuItem />
			<ImportCSVMenuItem />
			<ImportPhotosCSVMenuItem />
		</GridToolbarExportContainer>
	);
}
